import { call, put, select, takeLatest, takeEvery, all, race, take } from 'redux-saga/effects';
import * as T from './ReportsChatbotConstants';
import * as actions from './ReportsChatbotActions';
import {
    chatbotStartSessionApi,
    sendMerchantInfoApi,
    classifyQueryApi,
    metricsApi,
    insightsApi,
    chatHistoryApi,
    resumeSessionApi,
    storeMessageApi,
} from './ReportsChatbotApi';
import type {
    ClassifyQuerySuccessPayload,
    ConvMessage,
    ConvMessageData,
    MetricsDataInterface,
    InsightsDataInterface,
    ChatHistoryMessageInterface,
    ChatHistorySessionInterface,
    SessionResumeResponseInterface,
} from '../../../interface/ReportsChatbotInterface';
import type { RootState } from '../../rootReducer';

// ── Merchant Info ─────────────────────────────────────────────────────────────

function* fetchMerchantInfoSaga(
    action: ReturnType<typeof actions.fetchMerchantInfoRequest>
) {
    try {
        const data: any = yield call(sendMerchantInfoApi, action.payload);
        yield put(actions.fetchMerchantInfoSuccess(data));
    } catch (err: any) {
        yield put(actions.fetchMerchantInfoFailure(err?.message || 'Failed to fetch merchant info'));
    }
}

// ── Session ───────────────────────────────────────────────────────────────────

function* startSessionSaga(
    action: ReturnType<typeof actions.startSessionRequest>
) {
    try {
        const data: any = yield call(chatbotStartSessionApi, action.payload);
        yield put(actions.startSessionSuccess(data));
        // Add welcome message only when conversation is empty
        const conversation: ConvMessage[] = yield select(
            (state: RootState) => state.ReportsChatbot.conversation
        );
        if (data?.message && conversation.length === 0) {
            yield put(actions.addMessage({ type: 'welcome', id: 'welcome-1', text: data.message }));
        }
    } catch (err: any) {
        yield put(actions.startSessionFailure(err?.message || 'Failed to start session'));
    }
}

// ── Chat History ──────────────────────────────────────────────────────────────

function* fetchChatHistorySaga(
    action: ReturnType<typeof actions.fetchChatHistoryRequest>
) {
    try {
        const data: any = yield call(chatHistoryApi, action.payload);
        yield put(actions.fetchChatHistorySuccess(data));
    } catch (err: any) {
        yield put(actions.fetchChatHistoryFailure(err?.message || 'Failed to fetch chat history'));
    }
}

// ── Resume Session (Recent Insights) ─────────────────────────────────────────

function* resumeSessionSaga(
    action: ReturnType<typeof actions.resumeSessionRequest>
) {
    try {
        const data: SessionResumeResponseInterface = yield call(resumeSessionApi, action.payload);

        // Reuse ChatHistory's existing renderHistoryMessage (role/content/timestamp/type/metrics)
        // so a resumed session renders identically to a bulk-fetched history session.
        const messages: ChatHistoryMessageInterface[] = (data.messages || []).map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp,
            type: null,
            metrics: m.metrics ?? null,
            card: m.card ?? null,
        }));

        const session: ChatHistorySessionInterface = {
            session_id: data.session_id,
            user_name: data.user_name,
            locationid: data.locationid,
            created_at: data.created_at,
            last_updated: data.last_updated,
            preview: data.session_title ?? null,
            message_count: messages.length,
            messages,
        };

        yield put(actions.resumeSessionSuccess(session));
    } catch (err: any) {
        yield put(actions.resumeSessionFailure(err?.message || 'Failed to resume session'));
    }
}

// ── Send Query (classify → metrics? → insights) ────────────────────────────────

function* executeQueryFlow(payload: ReturnType<typeof actions.sendQueryRequest>['payload']) {
    const { query, sessionId, turnId, locationId, apiFailureResponse, target = 'main' } = payload;

    // Classify
    yield put(actions.setClassifyLoading(true));
    let classified: ClassifyQuerySuccessPayload | null = null;
    try {
        classified = (yield call(classifyQueryApi, { session_id: sessionId, query })) as ClassifyQuerySuccessPayload;
    } catch {
        // fall through with null
    }
    yield put(actions.setClassifyLoading(false));

    if (!classified) return;

    if (classified.is_greeting && classified.greeting_reply) {
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-reply`, text: classified.greeting_reply }, target));
        // generate_sql_and_data/generate_insight never run for a greeting, so persist this turn explicitly
        yield call(persistUiMessage, sessionId, 'user', query);
        yield call(persistUiMessage, sessionId, 'assistant', classified.greeting_reply);
        return;
    }

    if (classified.is_ambiguous && classified.clarifying_question) {
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-reply`, text: classified.clarifying_question }, target));
        // generate_sql_and_data/generate_insight never run for an ambiguous reply, so persist this turn explicitly
        yield call(persistUiMessage, sessionId, 'user', query);
        yield call(persistUiMessage, sessionId, 'assistant', classified.clarifying_question);
        return;
    }

    const dataId = `${turnId}-data`;
    const insightsId = `${turnId}-insights`;

    if (classified.needs_data) {
        yield put(actions.addMessage({ type: 'ai-data', id: dataId, status: 'loading' } as ConvMessageData, target));

        yield put(actions.setMetricsLoading(true));
        let metricsData: MetricsDataInterface | null = null;
        try {
            const result: any = yield call(metricsApi, { session_id: sessionId, locationid: locationId, query });
            metricsData = Array.isArray(result) ? result[0] : result;
        } catch {
            // fall through with null
        }
        yield put(actions.setMetricsLoading(false));

        if (!metricsData) {
            yield put(actions.updateMessage({ id: dataId, changes: { status: 'skip', errorMessage: apiFailureResponse } }, target));
        } else {
            const newStatus = resolveDataStatus(metricsData);
            yield put(actions.updateMessage({ id: dataId, changes: { status: newStatus, data: metricsData, errorMessage: undefined } }, target));
        }

        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage, target));

        yield put(actions.setInsightsLoading(true));
        const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
        yield put(actions.setInsightsLoading(false));

        yield put(actions.updateMessage({
            id: insightsId,
            changes: {
                status: insightsData ? 'success' : 'failure',
                data: insightsData ?? undefined,
                errorMessage: insightsData ? undefined : apiFailureResponse,
            },
        }, target));
    } else {
        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage, target));

        yield put(actions.setInsightsLoading(true));
        const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
        yield put(actions.setInsightsLoading(false));

        yield put(actions.updateMessage({
            id: insightsId,
            changes: {
                status: insightsData ? 'success' : 'failure',
                data: insightsData ?? undefined,
                errorMessage: insightsData ? undefined : apiFailureResponse,
            },
        }, target));
    }
}

function* sendQuerySaga(
    action: ReturnType<typeof actions.sendQueryRequest>
) {
    const { query, turnId, target = 'main' } = action.payload;

    yield put(actions.addMessage({ type: 'user', id: turnId, text: query }, target));

    const { wasCancelled } = yield race({
        task: call(executeQueryFlow, action.payload),
        wasCancelled: take(T.CANCEL_QUERY_REQUEST),
    });

    if (wasCancelled) {
        yield put(actions.cancelLoadingMessages());
        yield put(actions.addMessage({ type: 'ai-reply', id: `${turnId}-cancelled`, text: 'Request cancelled, retry.' }, target));
    }
}

// ── Retry Data (metrics + insights) ──────────────────────────────────────────

function* retryDataSaga(
    action: ReturnType<typeof actions.retryDataRequest>
) {
    const { messageId, sessionId, locationId, apiFailureResponse, target = 'main' } = action.payload;
    const turnId = messageId.replace(/-data$/, '') || messageId;
    const insightsId = `${turnId}-insights`;

    const conversation: ConvMessage[] = yield select(
        (state: RootState) => target === 'resume' ? state.ReportsChatbot.resumeLiveMessages : state.ReportsChatbot.conversation
    );
    const query = getQueryForMessage(conversation, messageId);

    yield put(actions.updateMessage({ id: messageId, changes: { status: 'loading', errorMessage: undefined } }, target));

    yield put(actions.setMetricsLoading(true));
    let metricsData: MetricsDataInterface | null = null;
    try {
        const result: any = yield call(metricsApi, { session_id: sessionId, locationid: locationId, query });
        metricsData = Array.isArray(result) ? result[0] : result;
    } catch {
        // fall through with null
    }
    yield put(actions.setMetricsLoading(false));

    if (!metricsData) {
        yield put(actions.updateMessage({ id: messageId, changes: { status: 'skip', errorMessage: apiFailureResponse } }, target));
    } else {
        const newStatus = resolveDataStatus(metricsData);
        yield put(actions.updateMessage({ id: messageId, changes: { status: newStatus, data: metricsData, errorMessage: undefined } }, target));
    }

    // Refresh conversation to check if insights bubble already exists
    const updatedConversation: ConvMessage[] = yield select(
        (state: RootState) => target === 'resume' ? state.ReportsChatbot.resumeLiveMessages : state.ReportsChatbot.conversation
    );
    const insightsExists = updatedConversation.some((m) => m.id === insightsId);

    if (insightsExists) {
        yield put(actions.updateMessage({ id: insightsId, changes: { status: 'loading', errorMessage: undefined } }, target));
    } else {
        yield put(actions.addMessage({ type: 'ai-insights', id: insightsId, status: 'loading' } as ConvMessage, target));
    }

    yield put(actions.setInsightsLoading(true));
    const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
    yield put(actions.setInsightsLoading(false));

    yield put(actions.updateMessage({
        id: insightsId,
        changes: {
            status: insightsData ? 'success' : 'failure',
            data: insightsData ?? undefined,
            errorMessage: insightsData ? undefined : apiFailureResponse,
        },
    }, target));
}

// ── Retry Insights only ───────────────────────────────────────────────────────

function* retryInsightsSaga(
    action: ReturnType<typeof actions.retryInsightsRequest>
) {
    const { messageId, sessionId, locationId, apiFailureResponse, target = 'main' } = action.payload;

    const conversation: ConvMessage[] = yield select(
        (state: RootState) => target === 'resume' ? state.ReportsChatbot.resumeLiveMessages : state.ReportsChatbot.conversation
    );
    const query = getQueryForMessage(conversation, messageId);

    yield put(actions.updateMessage({ id: messageId, changes: { status: 'loading', errorMessage: undefined } }, target));

    yield put(actions.setInsightsLoading(true));
    const insightsData: InsightsDataInterface | null = yield* fetchInsights(sessionId, locationId, query);
    yield put(actions.setInsightsLoading(false));

    yield put(actions.updateMessage({
        id: messageId,
        changes: {
            status: insightsData ? 'success' : 'failure',
            data: insightsData ?? undefined,
            errorMessage: insightsData ? undefined : apiFailureResponse,
        },
    }, target));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveDataStatus(metricsData: MetricsDataInterface): ConvMessageData['status'] {
    if (metricsData.has_metrics && metricsData.has_data) return 'metrics';
    if (!metricsData.has_metrics && metricsData.has_data) return 'detail';
    return 'skip';
}

function getQueryForMessage(conversation: ConvMessage[], messageId: string): string {
    const idx = conversation.findIndex((m) => m.id === messageId);
    for (let i = idx - 1; i >= 0; i--) {
        const m = conversation[i];
        if (m.type === 'user') return m.text;
    }
    return '';
}

function* persistUiMessage(sessionId: string, role: 'user' | 'assistant', content: string) {
    try {
        yield call(storeMessageApi, { session_id: sessionId, role, content });
    } catch {
        // best-effort persistence; the message is already shown locally regardless
    }
}

function* fetchInsights(
    sessionId: string,
    locationId: string,
    query: string
): Generator<any, InsightsDataInterface | null, any> {
    try {
        const result: any = yield call(insightsApi, { session_id: sessionId, locationid: locationId, query });
        return Array.isArray(result) ? result[0] : result;
    } catch {
        return null;
    }
}

// ── Root watcher ──────────────────────────────────────────────────────────────

export default function* ReportsChatbotSaga() {
    yield all([
        takeLatest(T.FETCH_MERCHANT_INFO_REQUEST, fetchMerchantInfoSaga),
        takeLatest(T.START_SESSION_REQUEST, startSessionSaga),
        takeLatest(T.FETCH_CHAT_HISTORY_REQUEST, fetchChatHistorySaga),
        takeLatest(T.RESUME_SESSION_REQUEST, resumeSessionSaga),
        takeLatest(T.SEND_QUERY_REQUEST, sendQuerySaga),
        takeEvery(T.RETRY_DATA_REQUEST, retryDataSaga),
        takeEvery(T.RETRY_INSIGHTS_REQUEST, retryInsightsSaga),
    ]);
}
